import { useState } from "react";
import { Chip, Menu, MenuItem, Typography, alpha } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { TaskStatus, TASK_STATUS_CHOICES } from "@/types";
import { getChipBase } from "./taskChipStyles";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { ChipSize, UIComponentSize } from "@/types/shared/uiComponents";

interface StatusSelectorProps {
  /** Currently selected status */
  value: TaskStatus;
  /** Callback when selection changes */
  onChange: (status: TaskStatus) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Chip size variant */
  size?: ChipSize;
}

export function StatusSelector({
  value,
  onChange,
  disabled = false,
  size = UIComponentSize.Medium,
}: StatusSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleSelect = (status: TaskStatus) => {
    onChange(status);
    setAnchorEl(null);
  };

  const colorType = getStatusColor(value);

  // Valid MUI palette colors that have .main
  const paletteColors = ["success", "warning", "error", "info", "primary", "secondary"] as const;
  type PaletteColorKey = (typeof paletteColors)[number];

  const isPaletteColor = (color: string): color is PaletteColorKey => paletteColors.includes(color as PaletteColorKey);

  return (
    <>
      <Chip
        label={formatToTitleCase(value)}
        onClick={handleClick}
        size="small"
        sx={{
          ...getChipBase({ size }),
          bgcolor: (theme) => {
            if (isPaletteColor(colorType)) {
              return alpha(theme.palette[colorType].main, 0.1);
            }
            return alpha(theme.palette.grey[500], 0.1);
          },
          color: isPaletteColor(colorType) ? `${colorType}.main` : "text.secondary",
          cursor: disabled ? "default" : "pointer",
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 140 },
          },
        }}
      >
        {TASK_STATUS_CHOICES.map((opt) => {
          const isSelected = value === opt.id;
          return (
            <MenuItem key={opt.id} selected={isSelected} onClick={() => handleSelect(opt.id)}>
              {isSelected && <CheckIcon fontSize="small" sx={{ mr: 1 }} />}
              <Typography sx={{ ml: isSelected ? 0 : 3.5 }}>{opt.name}</Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
